import type { ReactNode } from "react";
import { Dialog, styled, VisuallyHidden, XStack, YStack } from "tamagui";
import { ChevronLeft as ChevronLeftIcon, X as XIcon } from "@tamagui/lucide-icons";
import { spaceTokens } from "../../themes/token-space";
import { Button } from "../Button";
import { ProgressBar } from "../ProgressBar";
import { Body1, Body3, Display3, Title5 } from "../Typography";
export type NavHeaderSubtitleVariant = "titleSubtitle" | "titleSubtitleLeft" | "largeTitle";
export type NavHeaderNoSubtitleVariant = "default" | "compactLarge";
export type NavHeaderProgressBarVariant = "progressBar";

/**
 * Variant for the NavHeader.
 *
 * - "default": Centered title with left/right action buttons
 * - "titleSubtitle": Centered title with subtitle below
 * - "titleSubtitleLeft": Left-aligned title and subtitle after left button
 * - "largeTitle": Two-row layout with controls + large centered title/subtitle below
 * - "compactLarge": Display font title in a compact single row
 * - "progressBar": Progress bar in center replacing the title, with left/right action buttons
 */
export type NavHeaderVariant = NavHeaderSubtitleVariant | NavHeaderNoSubtitleVariant | NavHeaderProgressBarVariant;
type BaseNavHeaderProps = {
  title: string;
  showBackButton?: boolean;
  onBackPress?: () => void;
  hideCloseButton?: boolean;
  onClosePress?: () => void;
  /**
   * Whether this header is being used inside a Tamagui Dialog.
   * When true, wraps the title in Dialog.Title for accessibility.
   * @default false
   */
  isDialogHeader?: boolean;
};
type NavHeaderPropsWithSubtitle = BaseNavHeaderProps & {
  variant: NavHeaderSubtitleVariant;
  subtitle: string;
};
type NavHeaderPropsWithoutSubtitle = BaseNavHeaderProps & {
  variant?: NavHeaderNoSubtitleVariant;
  subtitle?: never;
};
type NavHeaderPropsWithProgressBar = BaseNavHeaderProps & {
  variant: NavHeaderProgressBarVariant;
  subtitle?: never;
  progress: number;
};
export type NavHeaderProps = NavHeaderPropsWithSubtitle | NavHeaderPropsWithoutSubtitle | NavHeaderPropsWithProgressBar;
export const HeaderContainer = styled(XStack, {
  width: "100%",
  height: "$si7",
  alignItems: "center",
  paddingHorizontal: "$sp2.5"
});
const CompactLargeContainer = styled(XStack, {
  width: "100%",
  height: "$si7",
  alignItems: "center",
  gap: "$sp2",
  paddingLeft: "$sp3",
  paddingRight: "$sp2.5"
});
const TitleSubtitleLeftContainer = styled(XStack, {
  width: "100%",
  height: "$si7",
  alignItems: "center",
  gap: "$sp1.5",
  paddingHorizontal: "$sp2.5"
});
export const HeaderSide = styled(YStack, {
  width: "$si4",
  height: "$si4",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  overflow: "visible"
});
export const CenterContent = styled(YStack, {
  flex: 1,
  alignItems: "center",
  justifyContent: "center"
});
const LargeTitleSection = styled(YStack, {
  width: "100%",
  alignItems: "center",
  gap: "$sp2",
  paddingHorizontal: "$sp3"
});
export const NavHeader = (props: NavHeaderProps) => {
  const {
    title,
    variant = "default",
    showBackButton = false,
    onBackPress,
    hideCloseButton = false,
    onClosePress,
    isDialogHeader = false
  } = props;
  // Offset button padding so the icon aligns to the content edge.
  const iconAlignOffset = -spaceTokens.sp2;
  const subtitle = props.variant !== "progressBar" ? props.subtitle : undefined;
  const leftItem = showBackButton ? <Button variant="tertiary" size="large" icon={ChevronLeftIcon} aria-label="Go back" onPress={onBackPress} marginLeft={iconAlignOffset} /> : null;
  const rightItem = !hideCloseButton ? <Button variant="tertiary" size="large" icon={XIcon} aria-label="Close" onPress={onClosePress} marginRight={iconAlignOffset} /> : null;

  // Wrap title in Dialog.Title for accessibility when in dialog.
  // Use tag="div" so our h2/h3 headings render correctly without nesting.
  const wrapForDialog = (content: ReactNode) => {
    if (isDialogHeader) {
      return <Dialog.Title render="div">{content}</Dialog.Title>;
    }
    return content;
  };
  if (props.variant === "progressBar") {
    return <HeaderContainer>
        <HeaderSide>{leftItem}</HeaderSide>
        <CenterContent>
          {isDialogHeader ? <VisuallyHidden>
              <Dialog.Title render="div">{title}</Dialog.Title>
            </VisuallyHidden> : null}
          <ProgressBar value={props.progress} width="$si10" />
        </CenterContent>
        <HeaderSide>{rightItem}</HeaderSide>
      </HeaderContainer>;
  }

  // Uses VisuallyHidden Dialog.Title for accessibility when in dialog
  if (variant === "compactLarge") {
    return <CompactLargeContainer>
        {isDialogHeader ? <VisuallyHidden>
            <Dialog.Title render="div">{title}</Dialog.Title>
          </VisuallyHidden> : null}
        <Display3 render="h2" color="$textDefault" flex={1}>
          {title}
        </Display3>
        {rightItem}
      </CompactLargeContainer>;
  }
  if (variant === "titleSubtitle") {
    return <HeaderContainer>
        <HeaderSide>{leftItem}</HeaderSide>
        <CenterContent>
          {wrapForDialog(<YStack alignItems="center">
              <Body1 render="h2" color="$textDefault" textAlign="center">
                {title}
              </Body1>
              <Body3 render="h3" color="$textSubtle" textAlign="center">
                {subtitle}
              </Body3>
            </YStack>)}
        </CenterContent>
        <HeaderSide>{rightItem}</HeaderSide>
      </HeaderContainer>;
  }
  if (variant === "titleSubtitleLeft") {
    return <TitleSubtitleLeftContainer>
        {leftItem}
        <YStack flex={1} justifyContent="center" paddingLeft={showBackButton ? "$sp0.5" : "$sp2"}>
          {wrapForDialog(<YStack>
              <Body1 render="h2" color="$textDefault">
                {title}
              </Body1>
              <Body3 render="h3" color="$textSubtle">
                {subtitle}
              </Body3>
            </YStack>)}
        </YStack>
        {rightItem}
      </TitleSubtitleLeftContainer>;
  }
  if (variant === "largeTitle") {
    return <YStack width="100%">
        <HeaderContainer>
          <HeaderSide>{leftItem}</HeaderSide>
          <CenterContent />
          <HeaderSide>{rightItem}</HeaderSide>
        </HeaderContainer>
        <LargeTitleSection>
          {wrapForDialog(<YStack width="100%" alignItems="center" gap="$sp1">
              <Title5 render="h2" color="$textDefault" textAlign="center">
                {title}
              </Title5>
              <Body1 render="h3" color="$textSubtle" textAlign="center">
                {subtitle}
              </Body1>
            </YStack>)}
        </LargeTitleSection>
      </YStack>;
  }
  return <HeaderContainer data-sentry-element="HeaderContainer" data-sentry-component="NavHeader" data-sentry-source-file="NavHeader.tsx">
      <HeaderSide data-sentry-element="HeaderSide" data-sentry-source-file="NavHeader.tsx">{leftItem}</HeaderSide>
      <CenterContent data-sentry-element="CenterContent" data-sentry-source-file="NavHeader.tsx">
        {wrapForDialog(<Body1 render="h2" color="$textDefault" textAlign="center">
            {title}
          </Body1>)}
      </CenterContent>
      <HeaderSide data-sentry-element="HeaderSide" data-sentry-source-file="NavHeader.tsx">{rightItem}</HeaderSide>
    </HeaderContainer>;
};